/** Canonical mentor profiles — only verified mentors listed on QoreConnect */
export const FOUNDER_MENTOR_PROFILE_URL = 'https://www.moise-ugwiri.com';
export const DANY_KATAMBA_PROFILE_URL = 'https://www.dany-katamba.com';

export const FOUNDER_MENTOR = {
  _id: 'moise-ugwiri',
  name: 'Dr. Moise Ugwiri',
  firstName: 'Moise',
  lastName: 'Ugwiri',
  title: 'Industrial AI Engineer, Founder & Writer',
  company: 'QoreNova / STMicroelectronics',
  profileUrl: FOUNDER_MENTOR_PROFILE_URL,
  expertiseAreas: [
    'Industrial Engineering',
    'AI Applications',
    'Process Engineering',
    'Career Transition',
    'Leadership',
    'Startup Building',
  ],
  expertise: [
    'Industrial Engineering',
    'AI Applications',
    'Process Engineering',
    'Career Transition',
    'Leadership',
    'Startup Building',
  ],
  rating: 5.0,
  averageRating: 5.0,
  totalSessions: 0,
  sessionsCompleted: 0,
  availability: { isAvailable: true },
  bio:
    'Founder of QoreNova. I help engineers bridge academic knowledge and industry skills — from process engineering to AI and career growth.',
};

export const DANY_KATAMBA_MENTOR = {
  _id: 'dany-katamba',
  name: 'Dr. Dany Katamba',
  firstName: 'Dany',
  lastName: 'Katamba',
  title: 'PhD Aerospace Engineering, R&D Specialist',
  company: 'Aerospace R&D',
  profileUrl: DANY_KATAMBA_PROFILE_URL,
  expertiseAreas: [
    'Aerospace Engineering',
    'R&D',
    'AI & Data Science',
    'Flight Systems',
    'Research',
    'Technical Leadership',
  ],
  expertise: [
    'Aerospace Engineering',
    'R&D',
    'AI & Data Science',
    'Flight Systems',
    'Research',
    'Technical Leadership',
  ],
  rating: 5.0,
  averageRating: 5.0,
  totalSessions: 0,
  sessionsCompleted: 0,
  availability: { isAvailable: true },
  bio:
    'PhD in Aerospace Engineering and R&D specialist. I mentor engineers in aerospace, research methods, and applying AI to complex engineering problems.',
};

/** Ordered list of verified mentors shown across QoreConnect and Academy. */
export const CANONICAL_MENTORS = [FOUNDER_MENTOR, DANY_KATAMBA_MENTOR];

const MENTOR_MATCHERS = [
  { id: 'moise-ugwiri', match: (name) => name.includes('moise') && name.includes('ugwiri'), profile: FOUNDER_MENTOR },
  { id: 'dany-katamba', match: (name) => name.includes('dany') && name.includes('katamba'), profile: DANY_KATAMBA_MENTOR },
];

function matcherForMentor(mentor = {}) {
  const name = mentorDisplayName(mentor).toLowerCase();
  return MENTOR_MATCHERS.find((m) => m.match(name));
}

export function mentorRatingValue(mentor = {}, fallback = 0) {
  const raw = mentor?.rating ?? mentor?.averageRating;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (raw && typeof raw === 'object') {
    const n = Number(raw.average);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

export function mentorDisplayName(mentor = {}) {
  return (
    mentor.name ||
    `${mentor.firstName || ''} ${mentor.lastName || ''}`.trim() ||
    FOUNDER_MENTOR.name
  );
}

export function isCanonicalMentor(mentor = {}) {
  return Boolean(matcherForMentor(mentor));
}

/** @deprecated Use isCanonicalMentor */
export function isFounderMentor(mentor = {}) {
  return isCanonicalMentor(mentor);
}

export function extractMentorsFromResponse(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data?.mentors)) return res.data.mentors;
  if (Array.isArray(res?.mentors)) return res.mentors;
  if (Array.isArray(res?.data)) return res.data;
  return [];
}

export function extractList(res, key) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.[key])) return res[key];
  if (Array.isArray(res?.data?.[key])) return res.data[key];
  if (Array.isArray(res?.data)) return res.data;
  return [];
}

export function isBookableMentor(mentor = {}) {
  return /^[a-f0-9]{24}$/i.test(String(mentor._id || ''));
}

/** Approved API mentors first (canonical founders pinned), fallback to canonical cards if API is empty. */
export function resolveDisplayMentors(apiMentors = []) {
  const list = Array.isArray(apiMentors) ? apiMentors : [];
  if (list.length === 0) return CANONICAL_MENTORS;

  const used = new Set();
  const head = CANONICAL_MENTORS.map((canonical) => {
    const fromApi = list.find((m) => matcherForMentor(m)?.id === canonical._id);
    if (fromApi) {
      used.add(String(fromApi._id));
      return {
        ...canonical,
        ...fromApi,
        name: mentorDisplayName(fromApi),
        profileUrl: canonical.profileUrl,
      };
    }
    return canonical;
  });

  const rest = list
    .filter((m) => !used.has(String(m._id)) && !matcherForMentor(m))
    .map((m) => ({
      ...m,
      name: mentorDisplayName(m),
    }));

  return [...head, ...rest];
}

export function toFeaturedMember(mentor) {
  const profile = mentor || FOUNDER_MENTOR;
  return {
    _id: profile._id,
    id: profile._id,
    name: mentorDisplayName(profile),
    title: profile.title,
    company: profile.company,
    profileUrl: profile.profileUrl,
  };
}

export function resolveFeaturedMembers(members = []) {
  const fromApi = members.filter(isCanonicalMentor);
  if (fromApi.length > 0) {
    return resolveDisplayMentors(fromApi).map(toFeaturedMember);
  }
  return CANONICAL_MENTORS.map(toFeaturedMember);
}

/** Shape used by Academy Coaching & Mentoring cards. */
export function toCoachingMentorFormat(mentor) {
  const base = mentor || FOUNDER_MENTOR;
  const expertise = base.expertise || base.expertiseAreas || [];
  const isAvailable = base.availability?.isAvailable !== false
    && base.availability !== 'Busy';
  const rating = mentorRatingValue(base, 4.7);
  return {
    ...base,
    name: mentorDisplayName(base),
    expertise,
    availability: isAvailable ? 'Available' : 'Busy',
    rating,
    averageRating: rating,
    reviewCount: base.reviewCount ?? base.rating?.count ?? base.totalSessions ?? 0,
    profileUrl: base.profileUrl,
  };
}

export function resolveCoachingMentors(apiMentors = []) {
  return resolveDisplayMentors(apiMentors).map(toCoachingMentorFormat);
}